import React, { useCallback, useEffect, useState } from 'react';
import { Megaphone, Save, Loader2, RotateCcw, Sparkles } from 'lucide-react';
import { adminGetBannerStyle, adminSaveBannerStyle } from '../lib/api';

const DEFAULTS = { headline: '', cta: 'Shop Now', accent: '#c6a15b' };
const SWATCHES = ['#c6a15b', '#e7d3a1', '#d94f70', '#4a8c5b', '#5b7fc6', '#b05bc6', '#e08a3c', '#f7f3ec'];
const fieldCls = 'bg-[#0b0b0b] border border-[#c6a15b]/30 px-3 py-2.5 font-body text-sm text-[#f7f3ec] outline-none focus:border-[#c6a15b] w-full';

// Live preview using representative sample data
const SAMPLE = { percent: 15, label: 'Signature Reign & Queen\'s Choice' };

const BannerStyleControl = ({ flash }) => {
  const [style, setStyle] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const s = await adminGetBannerStyle();
      setStyle({ ...DEFAULTS, ...s });
    } catch (e) { flash?.(e.message); }
    setLoading(false);
  }, [flash]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await adminSaveBannerStyle(style);
      setStyle({ ...DEFAULTS, ...res });
      flash?.('Banner style saved — live on the storefront');
    } catch (e) { flash?.(e.message); }
    setSaving(false);
  };

  const accent = /^#[0-9a-fA-F]{6}$/.test(style.accent) ? style.accent : '#c6a15b';
  const previewHeadline = (style.headline || '').trim()
    ? style.headline.replace(/\{percent\}/gi, SAMPLE.percent).replace(/\{label\}/gi, SAMPLE.label)
    : `${SAMPLE.percent}% OFF ${SAMPLE.label}`;

  return (
    <div className="bg-[#141311] border border-[#c6a15b]/20 p-6 mb-10" data-testid="banner-style-control">
      <div className="flex items-center gap-2 mb-2">
        <Megaphone size={18} className="text-[#c6a15b]" />
        <h2 className="font-display text-2xl text-[#f7f3ec]">Sale Banner Style</h2>
      </div>
      <p className="font-body text-xs text-[#9c9587] mb-5">
        Customize the storefront sale banner for each drop. Use <span className="text-[#e7d3a1]">{'{percent}'}</span> and <span className="text-[#e7d3a1]">{'{label}'}</span> in your headline and they'll fill in automatically. Leave the headline blank to use the default.
      </p>

      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 size={22} className="text-[#c6a15b] animate-spin" /></div>
      ) : (
        <>
          {/* Live preview */}
          <div className="mb-6">
            <span className="overline text-[0.5rem] text-[#7a7365] mb-2 block">Live Preview</span>
            <div className="relative overflow-hidden bg-gradient-to-r from-[#0b0b0b] via-[#1c150a] to-[#0b0b0b]"
              style={{ borderBottom: `1px solid ${accent}66`, borderTop: `1px solid ${accent}22` }} data-testid="banner-preview">
              <div className="absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(to right, transparent, ${accent}99, transparent)` }} />
              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-2 text-center">
                <span className="flex items-center gap-1.5">
                  <Sparkles size={13} className="shrink-0" style={{ color: accent }} />
                  <span className="font-display text-sm sm:text-base tracking-wide" style={{ color: accent }} data-testid="banner-preview-headline">{previewHeadline}</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="overline text-[0.55rem] text-[#9c9587] hidden sm:inline">Ends in</span>
                  <span className="font-body text-xs sm:text-sm text-[#e7d3a1] tabular-nums">03d 20h 49m 10s</span>
                </span>
                <span className="overline text-[0.55rem] underline underline-offset-2 hidden sm:inline"
                  style={{ color: accent, textDecorationColor: `${accent}66` }}>{style.cta || 'Shop Now'}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex flex-col md:col-span-2">
              <span className="overline text-[0.5rem] text-[#7a7365] mb-1">Headline</span>
              <input value={style.headline}
                onChange={(e) => setStyle((s) => ({ ...s, headline: e.target.value }))}
                placeholder="{percent}% OFF {label} — leave blank for default"
                data-testid="banner-headline-input" className={fieldCls} maxLength={120} />
            </label>
            <label className="flex flex-col">
              <span className="overline text-[0.5rem] text-[#7a7365] mb-1">Button Text</span>
              <input value={style.cta}
                onChange={(e) => setStyle((s) => ({ ...s, cta: e.target.value }))}
                placeholder="Shop Now" data-testid="banner-cta-input" className={fieldCls} maxLength={30} />
            </label>
            <div className="flex flex-col">
              <span className="overline text-[0.5rem] text-[#7a7365] mb-1">Accent Color</span>
              <div className="flex items-center gap-2">
                <input type="color" value={accent}
                  onChange={(e) => setStyle((s) => ({ ...s, accent: e.target.value }))}
                  data-testid="banner-accent-color" className="h-11 w-12 bg-transparent border border-[#c6a15b]/30 cursor-pointer p-0.5" />
                <input value={style.accent}
                  onChange={(e) => setStyle((s) => ({ ...s, accent: e.target.value }))}
                  data-testid="banner-accent-hex" className={`${fieldCls} font-mono`} placeholder="#c6a15b" />
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {SWATCHES.map((c) => (
                  <button key={c} onClick={() => setStyle((s) => ({ ...s, accent: c }))}
                    data-testid={`banner-swatch-${c.slice(1)}`}
                    className={`w-6 h-6 rounded-full border transition-transform hover:scale-110 ${accent.toLowerCase() === c.toLowerCase() ? 'border-white' : 'border-[#c6a15b]/20'}`}
                    style={{ backgroundColor: c }} title={c} />
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-5">
            <button onClick={() => setStyle(DEFAULTS)} data-testid="banner-reset-btn"
              className="flex items-center gap-2 px-4 py-2.5 border border-[#c6a15b]/40 text-[#e7d3a1] overline text-[0.6rem] hover:bg-white/5 transition-colors">
              <RotateCcw size={14} /> Reset to Default
            </button>
            <button onClick={save} disabled={saving} data-testid="banner-save-btn"
              className="btn-gold flex items-center gap-2 px-6 py-2.5 disabled:opacity-60">
              <span className="flex items-center gap-2">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Banner</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default BannerStyleControl;
